import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import _ from 'lodash'
import { useHistory } from 'react-router-dom'
import classNames from 'classnames'
import { useParams } from 'react-router'

import Button from 'components/Button'
import { Body } from 'layouts/basic'
import Input from 'components/Forms/Input'
import Loader from 'components/Loader'
import useAuthStore from 'store/auth'

import { errorMessages, validPasswordLength, validRepeatPassword, validEmail } from 'components/Forms/Input/validate'

export default function Signup(props) {
  const { signup } = useAuthStore()
  const [errors, setErrors] = useState(false)
  const [isRegistered, setIsRegistered] = useState(false)
  const [sendingForm, setSendingForm] = useState(false)

  const validateForm = (data) => {
    const passwordValid = validPasswordLength(data.password1)
    const password2Valid = validRepeatPassword(data.password1, data.password2)
    const emailValid = validEmail(data.email)
    let errorsList = {}
    let isValidForm = true

    if (data.firstName.trim() === '') {
      errorsList['/data/attributes/firstName'] = errorMessages.emptyField
      isValidForm = false
    }
    if (data.lastName.trim() === '') {
      errorsList['/data/attributes/lastName'] = errorMessages.emptyField
      isValidForm = false
    }
    if (data.email.trim() === '') {
      errorsList['/data/attributes/email'] = errorMessages.emptyField
      isValidForm = false
    } else if (!emailValid) {
      errorsList['/data/attributes/email'] = errorMessages.validEmail
      isValidForm = false
    }
    if (data.password1.trim() === '') {
      errorsList['/data/attributes/password1'] = errorMessages.emptyField
      isValidForm = false
    } else if (!passwordValid) {
      errorsList['/data/attributes/password1'] = errorMessages.validPasswordLength
      isValidForm = false
    }
    if (data.password2.trim() === '') {
      errorsList['/data/attributes/password2'] = errorMessages.emptyField
      isValidForm = false
    } else if (!password2Valid) {
      errorsList['/data/attributes/password1'] = errorMessages.validRepeatPassword
      errorsList['/data/attributes/password2'] = ''
      isValidForm = false
    }

    if (Object.keys(errorsList).length) {
      setErrors(errorsList)
    }

    return isValidForm

  }

  const onSubmit = async e => {
    e.preventDefault()
    setSendingForm(true)
    const firstName = e.currentTarget.elements[0].value
    const lastName = e.currentTarget.elements[1].value
    const email = e.currentTarget.elements[2].value
    const password1 = e.currentTarget.elements[3].value
    const password2 = e.currentTarget.elements[4].value

    const isValidForm = validateForm({ firstName, lastName, email, password1, password2 })
    if (isValidForm) {
      const response = await signup(email, password1, password2, { firstName, lastName })
      if (response.errors) {
        setErrors(
          _.reduce(
            _.get(response, 'errors', [{ detail: 'Error' }]),
            (red, item) => ({ ...red, [item.source.pointer]: item.detail }),
            {},
          )
        )
      } else {
        setIsRegistered(true)
      }
    }
    setSendingForm(false)
  }

  const nameHasError = errors && '/data/attributes/firstName' in errors
  const lastNameHasError = errors && '/data/attributes/lastName' in errors
  const emailHasError = errors && '/data/attributes/email' in errors
  const password1HasError = errors && '/data/attributes/password1' in errors
  const password2HasError = errors && '/data/attributes/password2' in errors
  return (
    <Body>
      {isRegistered ? (
        <div className='max-w-sm m-auto prose-sm lg:prose-xl'>
          <img src='/logo_improvisa192.png' alt='Improvisa' className='m-auto' />
          <div className='text-center w-full'>
            <h2>Your account has been created.</h2>
            <p>We have sent a confirmation email to the email address entered.</p>
          </div>
        </div>
      ) : (
        <div className='flex max-w-sm m-auto prose-sm lg:prose-xl'>
          <form onSubmit={onSubmit} className='text-center w-full'>
            <img src='/logo_improvisa192.png' alt='Improvisa' className='m-auto' />
            {
              errors && '/data/attributes/firstName' in errors && (
                <span className='flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1'>
                  {errors['/data/attributes/firstName']}
                </span>
              )
            }
            <Input
              inputProps={{
                required: true,
                placeholder: 'Name',
              }}
              className={classNames([
                { 'border border-red-500': nameHasError }
              ])}
            />
            {
              lastNameHasError && (
                <span className='flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1'>
                  {errors['/data/attributes/lastName']}
                </span>
              )
            }
            <Input
              inputProps={{
                required: true,
                placeholder: 'Lastname',
              }}
              className={classNames([
                { 'border border-red-500': lastNameHasError }
              ])}
            />
            {
              emailHasError && (
                <span className='flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1'>
                  {errors['/data/attributes/email']}
                </span>
              )
            }
            <Input
              inputProps={{
                type: 'email',
                required: true,
                placeholder: 'Email',
              }}
              className={classNames([
                { 'border border-red-500': emailHasError }
              ])}
            />
            {
              password1HasError && (
                <span className='flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1'>
                  {errors['/data/attributes/password1']}
                </span>
              )
            }
            <Input
              inputProps={{
                type: 'password',
                required: true,
                placeholder: 'Password',
              }}
              className={classNames([
                { 'border border-red-500': password1HasError }
              ])}
            />
            {
              password2HasError && (
                <span className='flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1'>
                  {errors['/data/attributes/password2']}
                </span>
              )
            }
            <Input
              inputProps={{
                type: 'password',
                required: true,
                placeholder: 'Repeat Password',
              }}
              className={classNames([
                { 'border border-red-500': password2HasError }
              ])}
            />
            <Button type='submit' disabled={sendingForm} className={classNames([
              'w-full text-base inverted',
              { 'disabled': sendingForm }
            ])} >
              Signup
              <Loader
                className='inline-block text-primary-600'
                active={sendingForm}
              />
            </Button>
            <div>
            <Link to='/login' className='mt-8 text-blue-700 text-base inline-flex items-center font-semibold tracking-wide'>
              <span class="hover:underline">
                Already have an account? Sign in
              </span>
              <span class="text-xl ml-2">&#8594;</span>
            </Link>
          </div>
          </form>
        </div>
      )}
    </Body>
  )
}

export function ConfirmEmail(props) {
  const { confirmAccount } = useAuthStore()
  let { token } = useParams()
  const history = useHistory()

  React.useEffect(() => {
    confirmAccount(token)
    history.push('/login')
  }, []) // eslint-disable-line

  return (
    <Body>
      <div className='max-w-sm m-auto prose-sm lg:prose-xl'>
        <img src='/logo_improvisa192.png' alt='Improvisa' className='m-auto' />
        <div className='text-center w-full'>
          <h2>Wait a minute, we are activating your account.</h2>
        </div>
      </div>
    </Body>
  )
}
